import Alerts from '../alerts/alerts';
import Base from '../eventable';
import { getLogger } from '../logging/logger';
import { wait } from './utilities';

const logger = getLogger('ImgLoader');

class ImgLoader extends Base {
	constructor() {
		logger.log('[constructor]');

		super();
		Object.assign(this, { wait });
	}

	/**
	 * Loads an image asynchronously.
	 *
	 * This function accepts an image in one of the following formats:
	 *
	 * 1. An existing HTMLImageElement object.
	 * 2. A string representing the image URL.
	 *
	 * It returns a Promise that resolves with the loaded image or rejects with an error if the image fails to load.
	 *
	 * @param {HTMLImageElement|string} image - The image to load. Can be an existing image element or a URL string.
	 * @returns {Promise<HTMLImageElement>} A Promise that resolves with the loaded image or rejects with an error.
	 */
	loadImage(image) {
		logger.log('[loadImage]', { image });

		return new Promise((resolve, reject) => {
			// Create or reuse image
			/** @type {HTMLImageElement} */
			let img;
			if (image instanceof HTMLImageElement) {
				img = image;
			} else if (typeof image === 'string') {
				img = new Image();
				img.src = image;
			} else {
				reject(new Error('Invalid image'));
				return;
			}

			// Check if complete and set appropriate resovles
			if (img.complete) {
				resolve(img);
			} else {
				const onLoad = () => {
					img.removeEventListener('load', onLoad);
					img.removeEventListener('error', onError);
					resolve(img);
				};
				const onError = () => {
					img.removeEventListener('load', onLoad);
					img.removeEventListener('error', onError);
					Alerts.danger(`[IMAGE FAILED] "${img.src}"`);
					reject(image);
				};
				img.addEventListener('load', onLoad);
				img.addEventListener('error', onError);
			}
		});
	}

	/**
	 * Loads an array of images asynchronously in parallel.
	 *
	 * This function accepts an optional array of images. If no images are provided, it resolves immediately with an empty array.
	 *
	 * Each image can be in one of the following formats:
	 *
	 * 1. An existing HTMLImageElement object.
	 * 2. A string representing the image URL.
	 * 3. Any falsy value (like null, undefined, 0, "", etc.) will be ignored.
	 *
	 * The function returns a Promise that resolves with an array containing the loaded images.
	 * The order of images in the resulting array will match the order in the input array.
	 * Images that fail to load will be omitted from the resulting array.
	 *
	 * @param {Array<HTMLImageElement|string|*>} [images=[]] - The array of images to load. Defaults to an empty array.
	 * @returns {Promise<Array<HTMLImageElement>>} A Promise that resolves with an array of loaded images.
	 */
	loadImages(images = []) {
		logger.log('[loadImages]', { images });

		if (!images.length) {
			return Promise.resolve([]);
		}

		// Add non-falsey values to Set to eliminate duplicates.
		const nonEmptyImageSet = new Set(images.filter((image) => !!image));
		return Promise.all(Array.from(nonEmptyImageSet).map(this.loadImage.bind(this)));
	}

	/**
	 * The STB can get locked up by preloading all images at once. This function loads
	 * images sequentially and pauses in between which gives the device a chance to keep
	 * processing, catch up, and prevent the device from locking up completely while
	 * images are loading.
	 * @param {string[]} urls Array of URLs to be loaded for images
	 * @param {number} delay Amount of time to wait between loading each image
	 * @returns Promise that resolves when all images are loaded
	 */
	trickleLoad(urls = [], delay = 100) {
		logger.log('[trickleLoad]', { urls, delay });

		// setup persistent promise to work from
		if (!this.tricklePromise) {
			this.tricklePromise = Promise.resolve();
		}

		// Create promise chain starting with any existing chain
		this.tricklePromise = urls.reduce((prevPromise, url) => {
			// Load image then wait
			prevPromise.then(() => this.loadImage(url)).then(() => this.wait(delay));
			return this.tricklePromise;
		}, this.tricklePromise);

		return this.tricklePromise;
	}
}

export default new ImgLoader();
